import React, { useMemo, useState } from "react";
import { STRATEGY_CHANGES } from "./strategyChanges";

/* ===== Strategy Log — the audit trail of trading-behavior changes =====
   Rendered from strategyChanges.js (version-controlled). Every change to
   signal selection, risk control or trade management gets an entry there. */

const STATUS_STYLE = {
  ACTIVE:    { color: "#34d399", label: "● ACTIVE" },
  OBSERVING: { color: "#facc15", label: "◐ OBSERVING" },
  PROPOSED:  { color: "#a78bfa", label: "○ PROPOSED" },
  REVERTED:  { color: "#f87171", label: "✕ REVERTED" },
};
const CATEGORIES = ["Risk Control", "Signal Selection", "Trade Management", "Data & Analytics"];
const CAT_COLOR = {
  "Risk Control": "#f87171", "Signal Selection": "#60a5fa",
  "Trade Management": "#34d399", "Data & Analytics": "#a78bfa",
};

export default function StrategyLog() {
  const [catFilter, setCatFilter] = useState(null);
  const entries = useMemo(
    () => STRATEGY_CHANGES.filter(e => !catFilter || e.category === catFilter),
    [catFilter]
  );

  return (
    <div style={{ padding: "12px 16px", maxWidth: 980 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>Strategy Log
          <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 400, marginLeft: 8 }}>
            audit trail of trading-behavior changes — what changed, why, and what it did
          </span>
        </h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCatFilter(catFilter === c ? null : c)}
              style={{
                background: catFilter === c ? CAT_COLOR[c] : "var(--panel3)",
                color: catFilter === c ? "#fff" : "var(--muted2)",
                border: `1px solid ${catFilter === c ? CAT_COLOR[c] : "var(--border2)"}`,
                borderRadius: 14, padding: "3px 10px", fontSize: 11, cursor: "pointer",
              }}>{c}</button>
          ))}
        </div>
      </div>
      <div style={{ background: "var(--panel3)", border: "1px solid var(--border2)", borderRadius: 8, padding: "8px 12px", margin: "8px 0 12px", fontSize: 11.5, lineHeight: 1.7, color: "var(--dim2)" }}>
        <b style={{ color: "var(--text2)" }}>📖 Book naming convention</b> (agreed 05 Oct 2026) —
        <b> "the lab" / Never-Red / NR</b>: the isolated Never-Red paper lab only (own tab, refs <b>NR-n</b>; confined there — never in Events or Crypto Analytics).
        <b> Book names</b> (4h book, 1h book, 1d trail, OB, gold, shorts): the main crypto trader, refs <b>CR-n</b> on prod / <b>L-n</b> local.
        <b> "ribbon"/"suggestions"</b>: the evidence-gated ticket engine. <b>"guard"</b>: the giveback guard. <b>"fib"/"guarded"</b>: the Indian books.
        Default: unqualified strategy/risk instructions apply to the <b>main books only</b> — the lab changes only when explicitly named.
      </div>
      <div style={{ fontSize: 11, color: "var(--faint2)", marginBottom: 14 }}>
        {entries.length} entries{catFilter ? ` · ${catFilter}` : ""} · UI/analytics work is excluded unless it changes what gets traded
      </div>

      {/* timeline */}
      <div style={{ position: "relative", paddingLeft: 26 }}>
        <div style={{ position: "absolute", left: 8, top: 6, bottom: 6, width: 2, background: "var(--border2)" }} />
        {entries.map((e, i) => {
          const st = STATUS_STYLE[e.status] || STATUS_STYLE.ACTIVE;
          return (
            <div key={i} style={{ position: "relative", marginBottom: 18 }}>
              <div style={{ position: "absolute", left: -24, top: 6, width: 12, height: 12, borderRadius: "50%",
                background: CAT_COLOR[e.category] || "var(--muted2)", border: "2px solid var(--panel2)" }} />
              <div style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "var(--muted2)", fontVariantNumeric: "tabular-nums" }}>{e.date}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text2)" }}>{e.title}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: st.color, marginLeft: "auto", whiteSpace: "nowrap" }}>{st.label}</span>
                </div>
                <div style={{ fontSize: 10, marginTop: 4 }}>
                  <span style={{ color: CAT_COLOR[e.category], border: `1px solid ${CAT_COLOR[e.category]}44`, borderRadius: 10, padding: "1px 8px" }}>{e.category}</span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--dim2)", marginTop: 10, lineHeight: 1.55 }}>
                  <div><b style={{ color: "var(--text2)" }}>Why:</b> {e.why}</div>
                  <div style={{ marginTop: 6 }}><b style={{ color: "var(--text2)" }}>Change:</b> {e.change}</div>
                  {e.effect && <div style={{ marginTop: 6 }}><b style={{ color: "var(--text2)" }}>Effect:</b> {e.effect}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: "var(--faint2)", marginTop: 6 }}>
        Source: <code>client/src/strategyChanges.js</code> — version-controlled; every future strategy change appends an entry as part of the change itself.
      </div>
    </div>
  );
}
